import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError, getToken, setToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, obj = auth
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    // Iter 41 — CRITICAL: If returning from Emergent Google OAuth callback,
    // skip the /me check. AuthCallback will POST the session_id first and
    // then re-render this provider with the freshly-issued cookie/token.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    if (!getToken()) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => mounted && setUser(r.data))
      .catch(() => {
        if (mounted) {
          setToken(null);
          setUser(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) setToken(data.token);
      setUser({
        email: data.email,
        role: data.role,
        user_id: data.user_id,
        player_id: data.player_id,
      });
      return data;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Login failed");
      return null;
    }
  };

  const register = async ({ email, password, handle, country }) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, handle, country });
      if (data.token) setToken(data.token);
      setUser({
        email: data.email,
        role: data.role,
        user_id: data.user_id,
        player_id: data.player_id,
      });
      return data;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Register failed");
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setToken(null);
    setUser(false);
  };

  // back-compat
  const admin = user && user.role === "admin" ? user : user === null ? null : false;

  return (
    <AuthCtx.Provider value={{ user, admin, login, register, logout, error, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
